import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Folder, Clock } from 'lucide-react';
import { collection, getDocs, doc, setDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { generateId } from '../utils/helpers';
import Layout from '../components/Layout';

export default function HomePage() {
    const [projects, setProjects] = useState([]);
    const [reviews, setReviews] = useState([]); // All reviews for stats
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newProjectTitle, setNewProjectTitle] = useState('');
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load projects
            const projectsSnapshot = await getDocs(collection(db, 'projects'));
            const projectsList = [];
            projectsSnapshot.forEach((doc) => {
                projectsList.push({ id: doc.id, ...doc.data() });
            });
            projectsList.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
            setProjects(projectsList);

            // Load ALL reviews for stats (opt: optimize this later with counters)
            const reviewsSnapshot = await getDocs(collection(db, 'reviews'));
            const allReviews = [];
            reviewsSnapshot.forEach((doc) => {
                allReviews.push({ id: doc.id, ...doc.data() });
            });
            setReviews(allReviews);
        } catch (error) {
            console.error("Error loading data:", error);
        }
        setLoading(false);
    };

    const getProjectStats = (projectId) => {
        const projectReviews = reviews.filter(r => r.projectId === projectId);
        const totalVideos = projectReviews.length;
        const totalFeedbacks = projectReviews.reduce((sum, r) => sum + (r.feedbacks?.length || 0), 0);
        return { totalVideos, totalFeedbacks };
    };

    const handleCreateProject = async (e) => {
        e.preventDefault();
        if (!newProjectTitle.trim()) return;

        try {
            const projectId = generateId('proj');
            await setDoc(doc(db, 'projects', projectId), {
                title: newProjectTitle.trim(),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
            setNewProjectTitle('');
            setShowCreateModal(false);
            loadData();
        } catch (error) {
            console.error("Error creating project:", error);
        }
    };

    const handleDeleteProject = async (projectId, e) => {
        e.preventDefault(); // Prevent link nav
        e.stopPropagation();
        if (!window.confirm("Are you sure you want to delete this project and all its videos?")) return;

        try {
            // Delete all reviews of this project
            const q = query(collection(db, 'reviews'), where('projectId', '==', projectId));
            const reviewsSnapshot = await getDocs(q);
            const deletePromises = reviewsSnapshot.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(deletePromises);

            // Delete project
            await deleteDoc(doc(db, 'projects', projectId));
            setProjects(projects.filter(p => p.id !== projectId));
        } catch (error) {
            console.error("Error deleting project:", error);
        }
    };

    return (
        <Layout>
            <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <Folder className="text-cyan-400" /> My Projects
                </h2>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-cyan-500/25"
                >
                    <Plus size={18} /> New Project
                </button>
            </div>

            {loading ? (
                <div className="text-center py-20 text-gray-500">Loading projects...</div>
            ) : projects.length === 0 ? (
                <div className="text-center py-20 bg-white/5 rounded-2xl border border-dashed border-white/10">
                    <Folder size={48} className="mx-auto text-gray-600 mb-4" />
                    <p className="text-xl text-gray-400 mb-4">No projects yet</p>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="text-cyan-400 hover:text-cyan-300 font-medium"
                    >
                        Create your first project
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {projects.map((project) => {
                        const stats = getProjectStats(project.id);
                        return (
                            <Link
                                to={`/project/${project.id}`}
                                key={project.id}
                                className="group block bg-[#1a1a1a] hover:bg-[#202020] rounded-xl border border-white/10 hover:border-cyan-500/50 p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-cyan-900/10"
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-3 bg-cyan-500/10 rounded-lg group-hover:bg-cyan-500/20 text-cyan-400 transition-colors">
                                        <Folder size={24} />
                                    </div>
                                    <button
                                        onClick={(e) => handleDeleteProject(project.id, e)}
                                        className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>

                                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors truncate">
                                    {project.title}
                                </h3>

                                <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                                    <span>{stats.totalVideos} videos</span>
                                    <span>•</span>
                                    <span>{stats.totalFeedbacks} feedbacks</span>
                                </div>

                                <div className="pt-4 border-t border-white/5 flex items-center justify-between text-xs text-gray-500">
                                    <div className="flex items-center gap-1.5">
                                        <Clock size={12} />
                                        {new Date(project.updatedAt).toLocaleDateString()}
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-[#1a1a1a] rounded-2xl border border-white/10 w-full max-w-md p-6 shadow-2xl animate-in zoom-in-95 duration-200">
                        <h3 className="text-xl font-bold text-white mb-4">Create New Project</h3>
                        <form onSubmit={handleCreateProject}>
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-400 mb-2">Project Name</label>
                                <input
                                    type="text"
                                    autoFocus
                                    value={newProjectTitle}
                                    onChange={(e) => setNewProjectTitle(e.target.value)}
                                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                                    placeholder="e.g., Marketing Campaign Q1"
                                />
                            </div>
                            <div className="flex gap-3 justify-end">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="px-4 py-2 text-gray-400 hover:text-white font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!newProjectTitle.trim()}
                                    className="px-6 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Create Project
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
}
